import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiTrendingUp, FiRefreshCw, FiDownload, FiPrinter } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getBalanceSheet, getCategoryFund,
  getDayBook, getCashBook, getBankBook,
  getAccGoldRegister, exportReportTable,
  getTrialBalance, getIncomeExpenditure,
  getItemRegister, getBhandarRegister,
} from '../../services/api';
import PdfOptionsModal from '../../pdf/PdfOptionsModal';
import { today } from '../../utils/dateUtils';
import { isOpeningFinancialYear, getCurrentFinancialYear } from '../../utils/financialYears';
import { buildAccountingExportPayload } from '../../utils/reportExportPayload';
import { PageHeader, ExcelExportModal } from '../../components/common';
import { NAV, TOAST } from '../../constants/labels';
import {
  REPORT_TABS, DATE_RANGE_TABS, AS_ON_DATE_TABS,
  SINGLE_DATE_TABS, ACCOUNT_LEDGER_TAB,
} from './reportsConfig';
import BalanceSheetReport  from './reports/BalanceSheetReport';
import TrialBalanceReport  from './reports/TrialBalanceReport';
import IncomeExpenditureReport from './reports/IncomeExpenditureReport';
import AccountLedgerReport from './reports/AccountLedgerReport';
import CategoryFundReport  from './reports/CategoryFundReport';
import DayBookReport       from './reports/DayBookReport';
import CashBookReport      from './reports/CashBookReport';
import BankBookReport, { buildBankGroups } from './reports/BankBookReport';
import GoldRegisterReport  from './reports/GoldRegisterReport';
import ItemRegisterReport  from './reports/ItemRegisterReport';
import BhandarRegisterReport from '../MemberReports/reports/BhandarRegisterReport';
import styles from './Reports.module.css';

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const ReportsPage = () => {
  const {
    activeDerasarId,
    activeDerasar,
    visibleFinancialYears,
    activeFinancialYearId,
  } = useAuth();

  const [activeTab, setActiveTab]       = useState('category-fund');
  const [filterYear, setFilterYear]     = useState(null); // set to current year once options load
  const [fromDate, setFromDate]         = useState(firstOfMonth());
  const [toDate, setToDate]             = useState(today());
  const [asOnDate, setAsOnDate]         = useState(today());
  const [singleDate, setSingleDate]     = useState(today());
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(false);
  const [, setExportLoading] = useState('');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [ledgerExport, setLedgerExport] = useState(null); // set by AccountLedgerReport
  const [bankAccountId, setBankAccountId] = useState('');
  const [dayBookType, setDayBookType]     = useState('');
  const yearOptions = useMemo(() => visibleFinancialYears, [visibleFinancialYears]);

  const TAB_TO_PDF_TYPE = {
    'balance-sheet':  'balanceSheet',
    'trial-balance':  'trialBalance',
    'income-expenditure':    'incomeExpense',
    'category-fund':  'categoryFund',
    'day-book':       'dayBook',
    'cash-book':      'cashBook',
    'bank-book':      'bankBook',
    'gold-register':  'goldRegister',
    'item-register':  'itemRegister',
    'account-ledger': 'ledger',
  };
  const pdfType = TAB_TO_PDF_TYPE[activeTab];

  // Must be declared before pdfDocumentProps useMemo which references it
  const selectedYear = yearOptions.find((year) => String(year.id) === String(filterYear)) || null;

  const pdfDocumentProps = useMemo(() => {
    if (!data) return null;
    const yearLabel = selectedYear?.yearLabel || '';
    // Build human-readable filter string for the PDF header
    const filterParts = [];
    if (yearLabel) filterParts.push(`Financial Year: ${yearLabel}`);
    if (['income-expenditure', 'balance-sheet', 'trial-balance', 'cash-book', 'bank-book', 'gold-register', 'item-register', 'bhandar-register', 'category-fund', 'day-book'].includes(activeTab))
      filterParts.push(`From: ${fromDate}`, `To: ${toDate}`);

    let reportData = data;
    if (activeTab === 'bank-book') {
      const selectedBank = bankAccountId
        ? (data.bankAccounts || []).find((a) => String(a.id) === String(bankAccountId))
        : null;
      if (selectedBank) filterParts.push(`Bank: ${selectedBank.name}`);
      // Pre-compute bank groups so the PDF document receives them directly
      const allGroups = buildBankGroups(data);
      const bankGroups = selectedBank
        ? allGroups.filter((g) => String(g.account.id) === String(bankAccountId))
        : allGroups;
      reportData = { ...data, bankGroups };
      if (selectedBank) {
        reportData.rows = (data.rows || []).filter((r) => String(r.bankAccountId) === String(bankAccountId));
        reportData.bankFiltered = true;
      }
    }

    return {
      ...reportData,
      derasar:    activeDerasar?.derasarId,
      filters:    filterParts.join('  |  '),
      filterYear: yearLabel,
      asOnDate,
      fromDate,
      toDate,
    };
  }, [data, activeDerasar, selectedYear, activeTab, asOnDate, fromDate, toDate, bankAccountId]);

  const excelPayload = useMemo(() => {
    if (!data || activeTab === ACCOUNT_LEDGER_TAB) return null;
    return buildAccountingExportPayload({
      activeTab,
      data,
      activeDerasarId,
      yearOptions,
      filterYear,
      fromDate,
      toDate,
      asOnDate,
      bankAccountId,
    });
  }, [data, activeTab, activeDerasarId, yearOptions, filterYear, fromDate, toDate, asOnDate, bankAccountId]);

  // Default to the current financial year on first load.
  useEffect(() => {
    if (filterYear == null && yearOptions.length > 0) {
      const current = getCurrentFinancialYear(yearOptions);
      setFilterYear(String(current?.id ?? yearOptions[yearOptions.length - 1]?.id ?? ''));
    }
  }, [yearOptions, filterYear]);

  /* -- load reference data ------------------------------------------------ */
  useEffect(() => {
    if (!activeDerasarId) return;
    setFilterYear(activeFinancialYearId || '');
  }, [activeDerasarId, activeFinancialYearId]);

  useEffect(() => {
    if (!selectedYear) return;

    const start = (selectedYear.startDate || '').slice(0, 10);
    const end = (selectedYear.endDate || '').slice(0, 10);

    if (isOpeningFinancialYear(selectedYear)) {
      setFromDate('');
      if (end) {
        setToDate(end);
        setAsOnDate(end);
      }
      return;
    }

    if (start) setFromDate(start);
    if (end) {
      setToDate(end);
      setAsOnDate(end);
    }
  }, [selectedYear]);

  /* -- run report --------------------------------------------------------- */
  // Keep filter values in a ref so runReport can always read the latest values
  // without needing them in its dependency array. This prevents date-input changes
  // from triggering automatic re-fetches — the user must click Run.
  const filtersRef = useRef({});
  filtersRef.current = { filterYear, fromDate, toDate, asOnDate, singleDate };

  const runReport = useCallback(async () => {
    if (!activeDerasarId) return;
    const { filterYear, fromDate, toDate, asOnDate } = filtersRef.current;
    setLoading(true);
    setData(null);
    try {
      const p = { derasarId: activeDerasarId };
      if (filterYear) p.yearId = filterYear;

      const apiMap = {
        'balance-sheet': () => getBalanceSheet({ ...p, fromDate, toDate }),
        'trial-balance': () => getTrialBalance({ ...p, fromDate, toDate }),
        'income-expenditure':   () => getIncomeExpenditure({ ...p, fromDate, toDate }),
        'category-fund': () => getCategoryFund({ ...p, fromDate, toDate }),
        'day-book':      () => getDayBook({ ...p, fromDate, toDate }),
        'cash-book':     () => getCashBook({ ...p, fromDate, toDate }),
        'bank-book':     () => getBankBook({ ...p, fromDate, toDate }),
        'gold-register':    () => getAccGoldRegister({ ...p, fromDate, toDate }),
        'item-register':    () => getItemRegister({ ...p, fromDate, toDate }),
        'bhandar-register': () => getBhandarRegister({ ...p, fromDate, toDate }),
        // account-ledger is intentionally absent — component manages its own fetch
      };

      const res = await apiMap[activeTab]?.();
      setData(res?.data ?? null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  // Dates/year intentionally excluded — read from filtersRef at call time so
  // changing date inputs does not trigger an automatic API call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDerasarId, activeTab]);

  useEffect(() => { runReport(); }, [runReport]);

  /* -- download export ---------------------------------------------------- */
  const downloadExport = useCallback(async (format) => {
    if (!activeDerasarId || !data) return;
    setExportLoading(format);
    try {
      const payload = buildAccountingExportPayload({
        activeTab,
        data,
        activeDerasarId,
        yearOptions,
        filterYear,
        fromDate,
        toDate,
        asOnDate,
      });
      if (!payload) return;

      const res = await exportReportTable({ ...payload, format });
      const type = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}-report.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(format === 'excel' ? 'Failed to download Excel' : TOAST.pdfFailed);
    } finally {
      setExportLoading('');
    }
  }, [activeDerasarId, activeTab, filterYear, fromDate, toDate, asOnDate, data, yearOptions]);

  /* -- switch tab --------------------------------------------------------- */
  const switchTab = (id) => {
    setActiveTab(id);
    setData(null);
    if (id !== ACCOUNT_LEDGER_TAB) setLedgerExport(null);
    if (id !== 'bank-book') setBankAccountId('');
  };

  /* -- render active report ----------------------------------------------- */
  const renderReport = () => {
    // Account Ledger manages its own data fetching
    if (activeTab === ACCOUNT_LEDGER_TAB) return <AccountLedgerReport onExportReady={setLedgerExport} />;

    if (loading) return <div className="loading"><div className="spinner" /></div>;
    if (!data)   return <div className="empty-state">Run the report to see results.</div>;

    const reportMap = {
      'balance-sheet': <BalanceSheetReport data={data} />,
      'trial-balance': <TrialBalanceReport data={data} />,
      'income-expenditure':   <IncomeExpenditureReport data={data} />,
      'category-fund': <CategoryFundReport data={data} />,
      'day-book':      <DayBookReport data={data} filterType={dayBookType} />,
      'cash-book':     <CashBookReport data={data} />,
      'bank-book':     <BankBookReport data={data} bankAccountId={bankAccountId} />,
      'gold-register':    <GoldRegisterReport data={data} />,
      'item-register':    <ItemRegisterReport data={data} />,
      'bhandar-register': <BhandarRegisterReport receipts={Array.isArray(data) ? data : []} />,
    };

    return reportMap[activeTab] ?? null;
  };

  return (
    <div className="report-page" style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
      <PageHeader
        icon={<FiTrendingUp />}
        title={NAV.reports}
        actions={
          activeTab === ACCOUNT_LEDGER_TAB ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => ledgerExport?.openPdfModal()}
                disabled={!ledgerExport}
              >
                <FiPrinter /> PDF
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => ledgerExport?.openExcelModal()}
                disabled={!ledgerExport}
              >
                <FiDownload /> Excel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary btn-sm" onClick={runReport} disabled={loading}>
                <FiRefreshCw /> Run Report
              </button>
              {pdfType && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowPdfModal(true)}
                  disabled={!data}
                >
                  <FiPrinter /> PDF
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowExcelModal(true)}
                disabled={!data || !excelPayload}
              >
                <FiDownload /> Excel
              </button>
            </div>
          )
        }
      />

      {/* Tabs */}
      <div className={styles.tabBar}>
        {REPORT_TABS.map((t) => (
          <button
            key={t.id}
            className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
            onClick={() => switchTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Shared filters — hidden for account-ledger (it manages its own) */}
      {activeTab !== ACCOUNT_LEDGER_TAB && <div className={styles.filterRow}>
        <div className="form-group">
          <label>Financial Year</label>
          <select
            value={filterYear ?? ''}
            onChange={(e) => setFilterYear(e.target.value)}
          >
            {yearOptions.map((y) => <option key={y.id} value={y.id}>{y.yearLabel}</option>)}
          </select>
        </div>

        {AS_ON_DATE_TABS.includes(activeTab) && (
          <div className="form-group">
            <label>As on Date</label>
            <input type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} />
          </div>
        )}

        {SINGLE_DATE_TABS.includes(activeTab) && (
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)} />
          </div>
        )}

        {DATE_RANGE_TABS.includes(activeTab) && (
          <>
            <div className="form-group">
              <label>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </>
        )}

        {activeTab === 'day-book' && data?.rows?.length > 0 && (
          <div className="form-group">
            <label>Type</label>
            <select value={dayBookType} onChange={(e) => setDayBookType(e.target.value)}>
              <option value="">All Types</option>
              {[...new Set(data.rows.map((r) => r.entryType).filter(Boolean))].sort().map((t) => (
                <option key={t} value={t}>
                  {({ offering: 'Offering Receipt', payment: 'Receipt', expense: 'Payment', asset_transfer: 'Asset Transfer', opening_balance: 'Opening Balance', adjustment: 'Adjustment', reversal: 'Reversal', loan_received: 'Loan Received', loan_repayment: 'Loan Repayment', loan_given: 'Loan Given', asset_purchase: 'Asset Purchase', fund_transfer: 'Fund Transfer', cash_deposit: 'Cash Deposit', cash_withdrawal: 'Cash Withdrawal', donation_transfer: 'Donation Transfer' })[t] || t}
                </option>
              ))}
            </select>
          </div>
        )}

        {activeTab === 'bank-book' && data?.bankAccounts?.length > 0 && (
          <div className="form-group">
            <label>Bank Account</label>
            <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
              <option value="">All Banks</option>
              {data.bankAccounts.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name} ({a.accountCode})</option>
              ))}
            </select>
          </div>
        )}

<div className="form-group" style={{ alignSelf: 'flex-end' }}>
          <button className="btn btn-primary btn-sm" onClick={runReport} disabled={loading}>
            <FiRefreshCw /> Run
          </button>
        </div>
      </div>}

      {/* Report output */}
      {renderReport()}

      {showPdfModal && pdfType && pdfDocumentProps && (
        <PdfOptionsModal
          type={pdfType}
          documentProps={pdfDocumentProps}
          defaultEmailTo={activeDerasar?.derasarId?.contactEmail || ''}
          onClose={() => setShowPdfModal(false)}
        />
      )}
      {showExcelModal && excelPayload && (
        <ExcelExportModal
          onClose={() => setShowExcelModal(false)}
          onDownload={() => downloadExport('excel')}
          exportPayload={excelPayload}
          defaultEmailTo={activeDerasar?.derasarId?.contactEmail || ''}
        />
      )}
    </div>
  );
};

export default ReportsPage;
